import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Upload, Loader2, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { fmtTime } from "@/lib/sourceMeta";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

const WeekSlot = ({ week, data, reload }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: d } = await api.post(`/timetable/${week}/upload`, fd);
      const n = Object.values(d?.days ?? {}).reduce((a, l) => a + (l?.length ?? 0), 0);
      toast.success(`Week ${week}: ${n} lessons read`);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove Week ${week} timetable?`)) return;
    await api.delete(`/timetable/${week}`);
    reload();
  };

  return (
    <div data-testid={`timetable-week-${week}`} className="min-w-0 rounded-xl border border-slate-200 p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="font-semibold text-slate-900">Week {week}</p>
        <div className="flex items-center gap-2">
          <label className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold cursor-pointer transition-colors ${data ? "border border-slate-200 text-slate-700 hover:bg-slate-50" : "bg-slate-900 text-black hover:bg-slate-700"}`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}{busy ? "Reading…" : data ? "Replace" : "Upload photo"}
            <input data-testid={`timetable-upload-${week}`} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" disabled={busy} onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
          </label>
          {data && <button data-testid={`timetable-delete-${week}`} onClick={remove} aria-label="Remove" className="grid place-items-center w-8 h-8 rounded-lg border border-slate-200 text-slate-500 hover:bg-rose-600 hover:text-white transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>}
        </div>
      </div>
      {busy && <p className="mt-3 text-xs text-slate-500">Claude is reading your timetable. This takes about 20 seconds.</p>}
      {data ? (
        <div className="mt-3">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-x-2 gap-y-4">
            {DAYS.map((d) => (
              <div key={d} className="min-w-0">
                <p className="font-mono text-[11px] uppercase tracking-wider text-slate-500">{d.slice(0, 3)}</p>
                <ul className="mt-1 space-y-1">
                  {(data.days?.[d] ?? []).map((l, i) => (
                    <li key={i} className="rounded-md bg-sky-50 px-2 py-1 text-xs text-slate-800"><span className="text-slate-500">{l.start}</span> {l.subject}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs text-slate-500">Uploaded {fmtTime(data.uploaded_at)}</p>
        </div>
      ) : !busy && <p className="mt-2 text-sm text-slate-500">Upload a clear photo or screenshot of Week {week}.</p>}
    </div>
  );
};

export const TimetableCard = () => {
  const [t, setT] = useState(null);
  const load = () => api.get("/timetable").then((r) => setT(r.data ?? null)).catch(() => setT(null));
  useEffect(() => { load(); }, []);
  const setWeek = async (week) => {
    try { await api.put("/timetable/current-week", { week }); toast.success(`This week is Week ${week}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <article data-testid="connection-card-timetable" className="min-w-0 rounded-2xl bg-surface border border-slate-200 p-5 sm:p-6 lg:col-span-2">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-teal-50 text-teal-700"><CalendarDays className="w-5 h-5" /></span>
          <div><h3 className="font-semibold text-slate-900">School timetable</h3><p className="text-sm text-slate-500">Today's lessons and the next school day appear in every brief.</p></div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600">This week is</span>
          <div className="inline-flex rounded-xl bg-slate-100 p-1">
            {["A", "B"].map((w) => (
              <button key={w} data-testid={`current-week-${w}`} onClick={() => setWeek(w)} className={`rounded-lg px-3.5 py-1.5 text-sm font-semibold transition-colors ${t?.current_week === w ? "bg-slate-900 text-black" : "text-slate-600 hover:text-slate-900"}`}>Week {w}</button>
            ))}
          </div>
        </div>
      </div>
      <p data-testid="week-source-note" className="mt-2 text-xs text-slate-500">{t?.week_source === "calendar" ? "Set from your term dates. Tap the other week only if it's wrong." : "Switches automatically every Monday. Add your term dates below to skip holidays."}</p>
      <div className="mt-4 grid gap-3">
        {["A", "B"].map((w) => <WeekSlot key={w} week={w} data={t?.weeks?.[w]} reload={load} />)}
      </div>
    </article>
  );
};
